import React, { useEffect, useState } from 'react';
import { convertTimestampToDateTime } from '../../../../helper/helper';
import { OrderDetailsModel, OrderTypeEnum } from '../../../../interfaces/order.interface';
import { Toast } from '../../../../components/Common/Toast';
import Loader from '../../../../components/Common/Loader';
import { createPrintBatch, dispatchPrintBatch, setDispatchHold } from '../../PrintBatches/services/PrintBatches.service';
import {
  applyOrderStatusAction,
  fetchOrderDiagnostics,
  fetchOrderEvents,
  fetchProviderStatus,
  fetchReleaseChildren,
  ManualStatusAction,
  ProviderStatus,
  regeneratePrintPdf,
  ReleaseChild,
  shipSelectedOrder,
  syncOrderFromProvider,
} from '../services/PhysicalBookOrder.service';
import { OrderFulfillmentStatusEnum } from '../../../../interfaces/order.interface';
import FulfillmentStatusBadges, { Badge } from './FulfillmentStatusBadges';
import {
  calculateOrderTotal,
  fulfillmentStatusBadge,
  getSyncState,
  isPhysicalOrder,
  orderItemLabel,
  orderRouteLabel,
  orderTypeLabel,
  printPdfBadge,
  providerStatusBadge,
  syncStateLabel,
  Tone,
  toneClass,
} from '../helpers/orderDisplay';
import { printJobLabel, shipstationOrderUrl, stripePaymentUrl } from '../helpers/fulfillment';

interface OrderDetailsTableProps {
  selectedOrderDetails: OrderDetailsModel | null;
  // Order-history owner email (the customer whose orders we're viewing). Required for recovery actions.
  email?: string | null;
}


// Authoritative lifecycle phase (mirrors the server's /diagnostics fulfillmentPhase). Drives one
// calm, accurate status instead of inferring (and mis-flagging) from raw country/sync fields.
type FulfillmentPhase =
  | 'mirror'
  | 'shipped'
  | 'synced'
  | 'error'
  | 'awaiting_teacher_ship'
  | 'missing_address'
  | 'awaiting_send';

const PHASE_NOTE: Record<FulfillmentPhase, string | null> = {
  awaiting_teacher_ship:
    'Waiting for the teacher to ship this order. The shipping address, country, and fulfillment route are set when the teacher releases it — this is expected and no action is needed yet.',
  missing_address:
    'This order has no shipping address and is not a teacher-shipment order, so it can’t be sent. Add a shipping address before re-pushing.',
  mirror:
    'This is the customer’s copy of the order. Fulfillment and recovery are managed on the primary (teacher/owner) order.',
  synced: 'This order already reached a fulfillment provider. Re-pushing may create a duplicate shipment.',
  shipped: 'This order has already shipped. Re-pushing may create a duplicate shipment.',
  error: null,
  awaiting_send: null,
};

const Card: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`border border-gray-300 rounded-md p-4 ${className || ''}`}>
    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-600 mb-3">{title}</h3>
    {children}
  </div>
);

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="text-right font-medium break-words">{children ?? '-'}</span>
  </div>
);

const money = (v: any): string => {
  const n = Number(v);
  return isNaN(n) ? '-' : `$ ${n.toFixed(2)}`;
};

const OrderDetailsTable: React.FC<OrderDetailsTableProps> = ({ selectedOrderDetails, email }) => {
  const [diagnostics, setDiagnostics] = useState<any | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shipLoading, setShipLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showShipForm, setShowShipForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Release parent: live children list (statuses + print state) for the card table.
  const [releaseChildren, setReleaseChildren] = useState<ReleaseChild[] | null>(null);
  const [holdBusy, setHoldBusy] = useState(false);
  const [dispatchHold, setDispatchHoldState] = useState<boolean>(!!(selectedOrderDetails as any)?.dispatchHold);
  const [shipTracking, setShipTracking] = useState('');
  const [shipCarrier, setShipCarrier] = useState('');
  const [shipNote, setShipNote] = useState('');
  const [providerStatus, setProviderStatus] = useState<ProviderStatus | null>(null);
  const [providerLoading, setProviderLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [jobOutcome, setJobOutcome] = useState<'dispatched' | 'draft' | null>(null);
  const [printRetryLoading, setPrintRetryLoading] = useState(false);

  const order = selectedOrderDetails;
  const orderNumber = order?.orderNumber;

  const loadReleaseChildren = () => {
    if (!email || !orderNumber) return;
    fetchReleaseChildren(email, String(orderNumber)).then(setReleaseChildren);
  };
  // Unconditional hook (early returns exist below); the guard lives inside.
  useEffect(() => {
    if (order?.orderType === OrderTypeEnum.BULK_PRINT) loadReleaseChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.orderType, orderNumber, email]);

  const physical = isPhysicalOrder(order?.orderType);
  const isSubscription = order?.orderType === OrderTypeEnum.SUBSCRIPTION_PURCHASE;
  const isCredits = order?.orderType === OrderTypeEnum.CREDITS_PURCHASE;
  // A bulk-print order is an aggregate (no bookId of its own). The book-level diagnostics/recovery
  // don't apply to it — recovery happens on the individual child orders — so we skip them here.
  const isBulk = order?.orderType === OrderTypeEnum.BULK_PRINT;

  // Classroom orders keep the authoritative (primary) doc under the student's classroom email; the
  // copy under the teacher is a read-only mirror. When viewing a mirror, route diagnostics + recovery
  // to the PRIMARY owner (order.shippingEmail) so the teacher's copy isn't a dead-end. For everything
  // else recoveryEmail is just the passed-in owner email.
  const isMirror = order?.isPrimaryOrderDoc === false;
  const recoveryEmail = isMirror && order?.shippingEmail ? order.shippingEmail : email ?? null;

  // Child of a combined classroom release: its shipment (and most order actions) belong to the
  // RELEASE parent — the child keeps only per-book concerns (Regenerate PDF, read-only detail).
  const isReleaseChild =
    physical && !isBulk && !!order?.shipmentOrderNumber && String(order.shipmentOrderNumber) !== String(orderNumber);

  const loadDiagnostics = async () => {
    if (!physical || isBulk || !recoveryEmail || !orderNumber) return null;
    setDiagLoading(true);
    try {
      const diag = await fetchOrderDiagnostics(recoveryEmail, String(orderNumber));
      setDiagnostics(diag);
      return diag;
    } finally {
      setDiagLoading(false);
    }
  };

  // Live provider status (ShipStation's own orderStatus) for the Shipment card. Read-only.
  // Skipped entirely for orders that never reached a provider — no id to look up, and it spares
  // the shared ShipStation rate limit during triage sessions.
  const hasProviderId = !!(order?.shipstationOrderId || order?.luluPrintJobId);
  // Test Data fixtures carry a fake 'testdata-…' provider id (batchable without ShipStation) —
  // fetching live status for one can only fail, and the failure read as a real outage.
  const isFixtureProviderId = String(order?.shipstationOrderId || '').startsWith('testdata-');
  const loadProviderStatus = async (providerIdHint?: boolean) => {
    if (!physical || isBulk || !recoveryEmail || !orderNumber || isFixtureProviderId || !(hasProviderId || providerIdHint)) return;
    setProviderLoading(true);
    try {
      setProviderStatus(await fetchProviderStatus(recoveryEmail, String(orderNumber)));
    } finally {
      setProviderLoading(false);
    }
  };

  // After any status-changing action: refresh diagnostics, the timeline, AND the provider status,
  // so the new audit entry and provider state are visible immediately. Diagnostics go FIRST: after
  // a successful re-push the provider id exists only in the fresh diagnostics — the stale list-row
  // prop that gates the provider fetch still says "no provider".
  const refreshAll = async () => {
    const diag = await loadDiagnostics();
    await loadProviderStatus(!!(diag?.shipstationOrderId || diag?.luluPrintJobId));
    if (physical && !isBulk && recoveryEmail && orderNumber) {
      setEvents(await fetchOrderEvents(recoveryEmail, String(orderNumber)));
    }
  };

  useEffect(() => {
    let ignore = false;
    setDiagnostics(null);
    setEvents([]);
    if (!physical || isBulk || !recoveryEmail || !orderNumber) {
      return;
    }
    setDiagLoading(true);
    fetchOrderDiagnostics(recoveryEmail, String(orderNumber))
      .then((d) => {
        // Ignore a stale response if the admin already switched to another order.
        if (!ignore) setDiagnostics(d);
      })
      .finally(() => {
        if (!ignore) setDiagLoading(false);
      });
    // Status timeline is best-effort decoration — no spinner, no error toast on failure.
    fetchOrderEvents(recoveryEmail, String(orderNumber)).then((e) => {
      if (!ignore) setEvents(e);
    });
    // Live provider status — its own spinner; best-effort (null on unreachable provider).
    setProviderStatus(null);
    if (order?.shipstationOrderId || order?.luluPrintJobId) {
      setProviderLoading(true);
      fetchProviderStatus(recoveryEmail, String(orderNumber))
        .then((p) => {
          if (!ignore) setProviderStatus(p);
        })
        .finally(() => {
          if (!ignore) setProviderLoading(false);
        });
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryEmail, orderNumber, order?.orderType]);

  if (!order) {
    return <div>No order details available.</div>;
  }

  // Prefer server-authoritative route/country from diagnostics; fall back to the order object.
  const country = diagnostics?.shippingCountry ?? order.shippingCountry;
  const route = diagnostics?.route ? (diagnostics.route === 'shipstation' ? 'ShipStation' : 'Lulu') : orderRouteLabel(order);
  const sync = getSyncState(order);

  // One-click recovery for the PRINT leg, right where the error is seen: re-dispatches the
  // order's print job (server re-sends only failed members). Without this, recovering meant
  // knowing to leave the order card and find the job on the Print Jobs screen. (Its loading
  // state is declared with the other hooks, above the early returns.)
  const handleRetryPrintJob = async () => {
    const batchId = (order as any)?.printBatchId;
    if (!batchId) return;
    const ok = await dispatchPrintBatch(String(batchId), setPrintRetryLoading);
    if (ok) await refreshAll();
  };

  // Prefer the server-authoritative phase; fall back to a client derivation while diagnostics
  // is still loading so the card doesn't flash a wrong/alarming state.
  const clientPhase = (): FulfillmentPhase => {
    // Only a true dead-end (a mirror with no recoverable primary owner) stays 'mirror'; otherwise we
    // act on the primary, so fall through to the real lifecycle phase.
    if (isMirror && !order?.shippingEmail) return 'mirror';
    if (order?.status === 'shipped') return 'shipped';
    if (sync === 'synced') return 'synced';
    if (sync === 'error') return 'error';
    if (order?.waitToShip) return 'awaiting_teacher_ship';
    const hasAddr = !!(order?.shippingAddress1 || order?.shippingCity || order?.shippingZip || order?.shippingCountry);
    return hasAddr ? 'awaiting_send' : 'missing_address';
  };
  const phase: FulfillmentPhase = (diagnostics?.fulfillmentPhase as FulfillmentPhase) || clientPhase();
  const hasDestination: boolean = diagnostics?.hasDestination ?? !!country;
  // Canonical status drives which curated actions are offered (server re-validates regardless).
  const canonicalStatus = (diagnostics?.fulfillmentStatus ?? order?.fulfillmentStatus) as
    | OrderFulfillmentStatusEnum
    | undefined;
  const onTeacherHold = (diagnostics?.waitToShip ?? order?.waitToShip) === true;
  const isDoneOrCanceled =
    canonicalStatus === OrderFulfillmentStatusEnum.SHIPPED || canonicalStatus === OrderFulfillmentStatusEnum.CANCELED;
  // A route is only real once a send was attempted/done — never while the order is still pending.
  const showRouteInfo = hasDestination && ['error', 'synced', 'shipped'].includes(phase);
  // Re-push to a provider is only valid once a send applies — not while parked for the teacher,
  // missing an address, or on a mirror copy. An order already on a provider (synced/shipped, incl.
  // Lulu) stays enabled as a "Force re-push", which is how a Lulu order is converted to ShipStation.
  const repushDisabled = phase === 'awaiting_teacher_ship' || phase === 'missing_address' || phase === 'mirror';
  const repushIsForce = phase === 'synced' || phase === 'shipped';
  // Recovery tools (PDF / provider ids / re-push) only make sense once a send is relevant. For a
  // teacher-ship order (nothing to recover yet), a mirror copy, or a stuck address-less order, hide them.
  const showRecovery = ['awaiting_send', 'error', 'synced', 'shipped'].includes(phase);

  // Consolidated health: surface only abnormal/actionable diagnostics as badges; the reassuring
  // "all good" signals (synced + print-ready) collapse into a single green line.
  const problems: Array<{ label: string; tone: Tone }> = [];
  if (diagnostics?.resolvesById === false) problems.push({ label: 'Book not found by ID', tone: 'red' });
  if (diagnostics?.bookComplete === false)
    problems.push({
      label: `Incomplete${diagnostics?.bookIncompleteReasons?.length ? `: ${diagnostics.bookIncompleteReasons[0]}` : ''}`,
      tone: 'amber',
    });
  if (showRouteInfo && diagnostics?.countryRecognized === false)
    problems.push({ label: `Unrecognized country “${diagnostics?.shippingCountry || '—'}”`, tone: 'red' });
  if (showRouteInfo && diagnostics?.routeAmbiguous) problems.push({ label: 'Route mismatch on re-push', tone: 'amber' });

  // The Fulfillment card's health line is internal readiness only — the provider relationship
  // (synced / ShipStation status) lives in the Shipping Provider card below.
  const goodSummary = diagnostics?.bookComplete === true ? 'Print-ready' : null;

  // Create-print-job is an internal pipeline action: only a ready-to-dispatch, non-bulk,
  // non-held order NOT already in a print job can be batched (same gate as createBatch enforces).
  const batchable =
    canonicalStatus === OrderFulfillmentStatusEnum.READY_TO_DISPATCH &&
    !isBulk &&
    !dispatchHold &&
    !order?.printBatchId &&
    !jobOutcome;

  const providerBadge = providerStatusBadge(providerStatus?.providerStatus);

  const copyToClipboard = async (text?: string | null) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      Toast('Copied to clipboard');
    } catch {
      Toast('Could not copy', true);
    }
  };

  const handleRegeneratePdf = async () => {
    if (!recoveryEmail || !orderNumber) return;
    const url = await regeneratePrintPdf(recoveryEmail, String(orderNumber), setPdfLoading);
    // Show the new URL even if diagnostics never loaded (e.g. the diagnostics fetch had failed).
    if (url) setDiagnostics((prev: any) => ({ ...(prev || {}), pdfUrlPrint: url }));
  };

  const handleRepush = async () => {
    if (!recoveryEmail || !orderNumber) return;
    const result = await shipSelectedOrder(String(orderNumber), recoveryEmail, setShipLoading);
    if (result?.alreadySynced) {
      const confirmed = window.confirm(
        'This order has already been sent to a fulfillment provider. Re-pushing may create a DUPLICATE shipment. Are you sure you want to force re-push?',
      );
      if (confirmed) await shipSelectedOrder(String(orderNumber), recoveryEmail, setShipLoading, true);
    }
    // Full refresh — a successful re-push just assigned a provider id, and only refreshAll's
    // fresh-diagnostics hint lets the provider-status card load for it.
    await refreshAll();
  };

  // Bulk parent: re-push the whole bundle. The backend (force/ship → shipBulkPrintOrder) re-sends each
  // book and skips any already synced, so this can't duplicate shipments. Bulk lives under the owner
  // email passed in (the teacher/owner), so we use `email` here, not recoveryEmail.
  const handleRepushBundle = async () => {
    if (!email || !orderNumber) return;
    await shipSelectedOrder(String(orderNumber), email, setShipLoading);
    await refreshAll();
    loadReleaseChildren();
  };

  const readyChildren = (releaseChildren || []).filter(
    (child) => child.fulfillmentStatus === OrderFulfillmentStatusEnum.READY_TO_DISPATCH,
  );

  const handleReleasePrintJob = async () => {
    if (!readyChildren.length) return;
    if (!window.confirm(`Create a print job with the ${readyChildren.length} ready order(s) of this release and dispatch it now?`)) return;
    const { ok, batchId, rejected } = await createPrintBatch(
      readyChildren.map((child) => ({ ownerEmail: child.ownerEmail, orderNumber: String(child.orderNumber) })),
      setBatchLoading,
    );
    if (ok && batchId) {
      if (rejected?.length) {
        Toast(`Print job saved as draft — ${rejected.length} order(s) were rejected. Review it on Print Jobs.`, true);
      } else {
        const dispatched = await dispatchPrintBatch(batchId, setBatchLoading);
        if (!dispatched) Toast('Print job created but NOT dispatched — re-dispatch it from Print Jobs.', true);
      }
      loadReleaseChildren();
    }
  };

  const anyActionBusy = shipLoading || pdfLoading || syncLoading || actionLoading || batchLoading || printRetryLoading;

  const handleCreateBatch = async () => {
    if (!recoveryEmail || !orderNumber) return;
    if (!window.confirm('Create a print job for this order and dispatch it to the printer now?')) return;
    const { ok, batchId } = await createPrintBatch([{ ownerEmail: recoveryEmail, orderNumber: String(orderNumber) }], setBatchLoading);
    let dispatched = false;
    if (ok && batchId) {
      dispatched = await dispatchPrintBatch(batchId, setBatchLoading);
    }
    if (ok) {
      // The order prop is a stale snapshot from the list — flag locally so the button can't
      // invite a confusing second click before the admin navigates away. The badge must tell
      // the truth: a failed dispatch leaves a draft, not a job at the printer.
      setJobOutcome(dispatched ? 'dispatched' : 'draft');
      await refreshAll();
    }
  };

  const handleSync = async () => {
    if (!recoveryEmail || !orderNumber) return;
    await syncOrderFromProvider(recoveryEmail, String(orderNumber), setSyncLoading);
    await refreshAll();
  };

  const runStatusAction = async (
    action: ManualStatusAction,
    opts: { note?: string; trackingNumber?: string; carrier?: string } = {},
  ) => {
    if (!recoveryEmail || !orderNumber) return;
    const ok = await applyOrderStatusAction(recoveryEmail, String(orderNumber), action, opts, setActionLoading);
    if (ok) await refreshAll();
  };

  const handleConfirmMarkShipped = async () => {
    if (!window.confirm('Mark this order as shipped? This records the shipment here only — it does not create a label or shipment at the provider.')) return;
    await runStatusAction('mark_shipped', {
      trackingNumber: shipTracking.trim() || undefined,
      carrier: shipCarrier.trim() || undefined,
      note: shipNote.trim() || undefined,
    });
    setShowShipForm(false);
    setShipTracking('');
    setShipCarrier('');
    setShipNote('');
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Cancel this order? Bookkeeping only — this does NOT cancel the ShipStation/Lulu job.')) return;
    const note = window.prompt('Optional note (why?)')?.trim() || undefined;
    await runStatusAction('cancel', { note });
  };

  const handleResetError = async () => {
    if (!window.confirm('Clear the recorded error and return the order to Preparing? You can then re-push it.')) return;
    const note = window.prompt('Optional note (what was fixed?)')?.trim() || undefined;
    await runStatusAction('reset_error', { note });
  };

  const handleToggleDispatchHold = async () => {
    if (!recoveryEmail || !orderNumber) return;
    const next = !dispatchHold;
    if (!window.confirm(next ? 'Hold this order back from print dispatch (auto and manual batching will skip it)?' : 'Release the dispatch hold?')) return;
    const ok = await setDispatchHold(recoveryEmail, String(orderNumber), next, setHoldBusy);
    if (ok) setDispatchHoldState(next);
  };

  const handleReleaseHold = async () => {
    if (!window.confirm('Release the teacher hold? The order will not auto-ship — use Re-push after releasing.')) return;
    const note = window.prompt('Optional note (why?)')?.trim() || undefined;
    await runStatusAction('release_hold', { note });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone="blue">{orderTypeLabel(order?.orderType)}</Badge>
        <span className="text-lg font-bold">Order #{order?.orderNumber || '—'}</span>
        {/* Canonical lifecycle status (server-resolved; diagnostics wins over the list row).
            Non-physical orders keep their legacy status text. */}
        {physical ? (
          <FulfillmentStatusBadges
            status={diagnostics?.fulfillmentStatus ?? order?.fulfillmentStatus}
            actionNeeded={diagnostics?.actionNeeded ?? order?.fulfillmentActionNeeded}
          />
        ) : (
          order?.status && <Badge tone="gray">{String(order.status).replace(/_/g, ' ')}</Badge>
        )}
        {physical && sync === 'bundle' && <Badge tone="gray">{syncStateLabel(sync)}</Badge>}
        {physical &&
          !isBulk &&
          (() => {
            const pdf = printPdfBadge({
              printProcessingState: diagnostics?.printProcessingState ?? order?.printProcessingState,
              printProcessingError: diagnostics?.printProcessingError ?? (order as any)?.printProcessingError,
            });
            return pdf ? (
              <Badge tone={pdf.tone} title={pdf.title}>
                {pdf.label}
              </Badge>
            ) : null;
          })()}
      </div>

      {/* Release parent is an aggregate — book-level recovery doesn't apply; point to the children. */}
      {isBulk && (
        <Card title="Classroom release">
          <p className="text-sm text-gray-600">
            {order?.classroomName ? (
              <>
                Release for <b>{order.classroomName}</b> —{' '}
              </>
            ) : null}
            {order?.totalBooksInBulk ?? order?.bulkPrintOrderNumbers?.length ?? 0} orders
            {order?.shipstationOrderId
              ? ', shipped together as ONE combined ShipStation order under this release number.'
              : '.'}{' '}
            Per-book print state lives on the individual orders — open them from the list.
          </p>
          {order?.shipstationOrderId && shipstationOrderUrl(orderNumber) && (
            <p className="text-sm mt-2">
              <span className="inline-block rounded bg-storyblue/10 text-storyblue px-2 py-0.5 text-xs font-semibold mr-2">
                📦 {order.shipstationOrderId}
              </span>
              <a
                href={shipstationOrderUrl(orderNumber)!}
                target="_blank"
                rel="noreferrer"
                className="text-storyblue text-xs font-semibold underline"
                title="Open the combined shipment in the ShipStation web app"
              >
                Open in ShipStation ↗
              </a>
            </p>
          )}
          {releaseChildren && releaseChildren.length > 0 && (
            <div className="mt-3 border rounded-lg divide-y">
              {releaseChildren.map((child) => {
                const badge = fulfillmentStatusBadge(child.fulfillmentStatus);
                const pdf = printPdfBadge(child);
                return (
                  <div key={child.orderNumber} className="flex items-center justify-between px-3 py-1.5 text-sm gap-2">
                    <span className="truncate">
                      <span className="font-semibold mr-2">#{child.orderNumber}</span>
                      {child.bookTitle || child.bookId || '—'}
                      {child.quantity > 1 ? ` ×${child.quantity}` : ''}
                    </span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      {pdf && (
                        <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${toneClass[pdf.tone]}`} title={pdf.title}>
                          {pdf.label}
                        </span>
                      )}
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {readyChildren.length > 0 && (
              <button
                type="button"
                disabled={batchLoading}
                onClick={handleReleasePrintJob}
                className="bg-storyblue text-white font-semibold rounded-md px-4 py-2 disabled:opacity-50 flex items-center gap-2"
              >
                {batchLoading && <Loader loader={true} dimentions={'w-5 h-5'} />}
                Create & dispatch print job ({readyChildren.length} ready)
              </button>
            )}
            <button
              type="button"
              disabled={shipLoading}
              onClick={handleRepushBundle}
              className="bg-storygreen text-white font-semibold rounded-md px-4 py-2 disabled:opacity-50 flex items-center gap-2"
            >
              {shipLoading && <Loader loader={true} dimentions={'w-5 h-5'} />}
              Re-push release to Shipping
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Safe to repeat for ShipStation-routed books: a release already at ShipStation is skipped (no duplicate
              shipments) and finished print PDFs are left untouched — only what never reached the provider is pushed.
              The toast reports exactly what was pushed, skipped, or failed.
            </p>
          </div>
        </Card>
      )}

      {/* Fulfillment & Recovery — physical, non-bulk orders only, where the customer email is wired
          (the dedicated Physical Book Order screen). Hidden elsewhere so no dangling message shows. */}
      {physical && recoveryEmail && !isBulk && (
        <>
          {/* ── Internal: our order + print pipeline ─────────────────────────────── */}
          <Card title="Fulfillment">
            {isMirror && order?.shippingEmail && (
              <p className="text-xs text-blue-700 mb-3 bg-blue-50 rounded px-2 py-1">
                This is the teacher’s copy. Actions apply to the <b>primary order</b> under <b>{order.shippingEmail}</b>.
              </p>
            )}

            {/* Health — internal readiness. Problems surface as badges; else a green print-ready line. */}
            <div className="flex flex-wrap gap-2 items-center mb-3">
              {printJobLabel(order) && (
                <Badge tone="gray" title="Print job — the batch sent to the printer (see Print Jobs)">
                  🖨 Print job {printJobLabel(order)}
                </Badge>
              )}
              {jobOutcome === 'dispatched' && <Badge tone="green">Print job dispatched — track it in Print Jobs</Badge>}
              {jobOutcome === 'draft' && <Badge tone="amber">Print job created but NOT dispatched — re-dispatch it from Print Jobs</Badge>}
              {dispatchHold && <Badge tone="amber">On dispatch hold</Badge>}
              {problems.length > 0
                ? problems.map((p, i) => (
                    <Badge key={i} tone={p.tone}>
                      {p.label}
                    </Badge>
                  ))
                : goodSummary && (
                    <span className="text-sm font-semibold text-green-700 flex items-center gap-1">
                      <span aria-hidden>✓</span> {goodSummary}
                    </span>
                  )}
              {diagLoading && <Loader loader={true} dimentions={'w-5 h-5'} />}
            </div>

            {PHASE_NOTE[phase] && phase !== 'synced' && phase !== 'shipped' && (
              <p className={`text-xs mb-3 ${phase === 'missing_address' ? 'text-red-700' : 'text-gray-500'}`}>
                {PHASE_NOTE[phase]}
              </p>
            )}
            {diagnostics?.lastFulfillmentError && (
              <p className="text-xs text-red-700 mb-2">
                Last error: {diagnostics.lastFulfillmentError}
                {diagnostics.shipstationSyncFailedAt ? ` (${convertTimestampToDateTime(diagnostics.shipstationSyncFailedAt)})` : ''}
              </p>
            )}

            {/* Payment link — refunds/cancellations are handled manually in the Stripe dashboard;
                nothing in our backend refunds. Absent on free orders and pre-linkage orders. */}
            {(() => {
              const paymentIntentId = diagnostics?.stripePaymentIntentId ?? order?.stripePaymentIntentId;
              const url = stripePaymentUrl(paymentIntentId);
              return url ? (
                <p className="text-xs mb-3">
                  <span className="text-gray-500 mr-2">Payment:</span>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-storyblue font-semibold underline"
                    title="Open this payment in the Stripe dashboard (refunds are manual there)"
                  >
                    {paymentIntentId} — view in Stripe ↗
                  </a>
                </p>
              ) : null;
            })()}

            {/* Internal actions — our pipeline + bookkeeping. Provider actions live in the card below.
                Release children keep only Regenerate PDF: everything else (print job, shipped,
                hold, cancel, provider pushes) is managed on the classroom release parent. */}
            <div className="flex flex-wrap gap-2 items-center border-t border-gray-200 pt-3 mt-2">
              {isReleaseChild && (
                <span className="text-xs text-gray-500">
                  Order actions are managed on classroom release <b>#{order?.parentBulkPrintOrderNumber || order?.shipmentOrderNumber}</b> — only the
                  print PDF is per-book here.
                </span>
              )}
              {batchable && !isReleaseChild && (
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={handleCreateBatch}
                  title="Creates a one-order print job and dispatches it to the printer immediately."
                  className="bg-storygreen text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {batchLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
                  Create & dispatch print job
                </button>
              )}
              {!isDoneOrCanceled && !isReleaseChild && (
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={() => setShowShipForm((v) => !v)}
                  title="Record the shipment here (bookkeeping only — no label is created at the provider)."
                  className="bg-storyblue text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Mark shipped…
                </button>
              )}
              {canonicalStatus === OrderFulfillmentStatusEnum.ERROR && (order as any)?.printBatchId && (
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={handleRetryPrintJob}
                  className="bg-storyblue text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
                  title="Re-sends this order's print job to the printer (only not-yet-printed members are re-sent)"
                >
                  {printRetryLoading
                    ? 'Retrying…'
                    : `Retry print job${(order as any)?.printBatchNumber ? ` #${(order as any).printBatchNumber}` : ''}`}
                </button>
              )}
              {/* Label-only clear stays available ONLY where no print retry exists (provider-leg
                  errors) — next to a real retry it was just a decoy. */}
              {canonicalStatus === OrderFulfillmentStatusEnum.ERROR && !(order as any)?.printBatchId && (
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={handleResetError}
                  className="bg-amber-600 text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
                  title="Clears the error label only — sends nothing anywhere"
                >
                  Clear error label
                </button>
              )}
              {onTeacherHold && !isDoneOrCanceled && (
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={handleReleaseHold}
                  className="bg-storyblue text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Release teacher hold
                </button>
              )}
              {order?.bookId && (
                <button
                  type="button"
                  disabled={pdfLoading}
                  onClick={handleRegeneratePdf}
                  title="Rebuilds the print PDF only — no email, no provider call."
                  className="bg-gray-600 text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {pdfLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
                  Regenerate PDF
                </button>
              )}
              {actionLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
            </div>

            {showShipForm && !isDoneOrCanceled && (
              <div className="mt-2 flex flex-wrap gap-2 items-center bg-gray-50 border border-gray-200 rounded-md p-2">
                <input
                  type="text"
                  value={shipTracking}
                  onChange={(e) => setShipTracking(e.target.value)}
                  placeholder="Tracking # (optional)"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-44"
                />
                <input
                  type="text"
                  value={shipCarrier}
                  onChange={(e) => setShipCarrier(e.target.value)}
                  placeholder="Carrier (optional)"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-32"
                />
                <input
                  type="text"
                  value={shipNote}
                  onChange={(e) => setShipNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-52"
                />
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={handleConfirmMarkShipped}
                  className="bg-storygreen text-white font-semibold rounded px-3 py-1 text-sm disabled:opacity-50"
                >
                  Confirm shipped
                </button>
                <button type="button" onClick={() => setShowShipForm(false)} className="text-gray-600 text-sm underline">
                  Cancel
                </button>
              </div>
            )}

            {/* Advanced — internal technical fields + rare/destructive actions. */}
            <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-xs text-gray-500 underline mt-3">
              {showAdvanced ? '▲ Hide advanced' : '▼ Advanced & technical'}
            </button>
            {showAdvanced && (
              <div className="mt-2 border-t border-gray-200 pt-3">
                {diagnostics?.printProcessingState && <Field label="Print state">{diagnostics.printProcessingState}</Field>}
                {diagnostics?.isLegacy && <Field label="Legacy order">Yes (pre-2025-12)</Field>}
                {order?.bookId && (
                  <Field label="Print PDF URL">
                    {diagnostics?.pdfUrlPrint ? (
                      <span className="flex items-center gap-2 flex-wrap justify-end">
                        <a href={diagnostics.pdfUrlPrint} target="_blank" rel="noreferrer" className="underline text-storygreen break-all">
                          {diagnostics.pdfUrlPrint}
                        </a>
                        <button type="button" className="bg-storyblue text-white rounded px-2 py-0.5 text-xs" onClick={() => copyToClipboard(diagnostics.pdfUrlPrint)}>
                          Copy
                        </button>
                      </span>
                    ) : (
                      <span className="text-gray-500">None — use “Regenerate PDF”.</span>
                    )}
                  </Field>
                )}
                <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t border-gray-200">
                  {!isDoneOrCanceled && !isReleaseChild && (
                    <button
                      type="button"
                      disabled={anyActionBusy || holdBusy}
                      onClick={handleToggleDispatchHold}
                      className={`${dispatchHold ? 'bg-amber-600' : 'bg-gray-500'} text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50`}
                    >
                      {dispatchHold ? 'Release dispatch hold' : 'Hold from dispatch'}
                    </button>
                  )}
                  {!isDoneOrCanceled && !isReleaseChild && (
                    <button
                      type="button"
                      disabled={anyActionBusy}
                      onClick={handleCancelOrder}
                      className="bg-red-600 text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
                    >
                      Cancel order
                    </button>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* ── Shipping provider: ShipStation / Lulu status + provider actions. Shown whenever a
              send is relevant OR the order already carries provider identity/tracking (e.g. a
              teacher-parked order that synced before being parked). ──────── */}
          {(showRecovery || hasProviderId || !!order?.trackingNumber) && (
            <Card title="Shipment">
              {/* Combined classroom shipment: this order travels in ONE package created under the
                  release parent's number — say so, or the shared ShipStation id reads as a bug. */}
              {order?.shipmentOrderNumber && String(order.shipmentOrderNumber) !== String(orderNumber) && (
                <p className="text-xs text-blue-700 mb-3 bg-blue-50 rounded px-2 py-1">
                  Part of the <b>combined classroom shipment</b> under release{' '}
                  <b>#{order?.parentBulkPrintOrderNumber || order.shipmentOrderNumber}</b>
                  {order?.shipmentSize ? ` (${order.shipmentSize} orders, one package)` : ''} — provider status and
                  tracking are shared with the whole release.
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-center mb-3">
                <Badge tone={route === 'ShipStation' ? 'gray' : 'amber'}>{providerStatus?.provider === 'lulu' ? 'Lulu' : route}</Badge>
                {(diagnostics?.shipstationOrderId || order?.shipstationOrderId) &&
                  shipstationOrderUrl(order?.shipmentOrderNumber ?? orderNumber) && (
                  <a
                    href={shipstationOrderUrl(order?.shipmentOrderNumber ?? orderNumber)!}
                    target="_blank"
                    rel="noreferrer"
                    className="text-storyblue text-xs font-semibold underline"
                    title="Open this shipment in the ShipStation web app"
                  >
                    Open in ShipStation ↗
                  </a>
                )}
                {isFixtureProviderId ? (
                  <span className="text-gray-400 text-sm">test fixture — no real ShipStation order behind this id</span>
                ) : providerLoading ? (
                  <span className="text-gray-400 text-sm flex items-center gap-1">
                    <Loader loader={true} dimentions={'w-4 h-4'} /> checking…
                  </span>
                ) : providerStatus?.provider === 'shipstation' && providerStatus.reachable && providerStatus.providerStatus ? (
                  <Badge tone={providerBadge.tone}>{providerBadge.label}</Badge>
                ) : providerStatus?.provider === 'shipstation' && !providerStatus.reachable ? (
                  <span className="text-gray-400 text-sm">couldn’t reach ShipStation</span>
                ) : providerStatus?.provider === 'lulu' ? (
                  <span className="text-gray-500 text-sm">{providerStatus.providerStatus || 'no status'} · integration dormant</span>
                ) : (
                  <span className="text-gray-400 text-sm">not sent to a provider yet</span>
                )}
              </div>

              <Field label="Our sync state">
                {/* 'Synced' alone reads as 'it shipped' — it only means the provider has the order. */}
                {sync === 'synced' && canonicalStatus !== OrderFulfillmentStatusEnum.SHIPPED
                  ? 'Synced — awaiting shipment'
                  : syncStateLabel(sync)}
              </Field>
              {(providerStatus?.providerOrderId || diagnostics?.shipstationOrderId || order?.shipstationOrderId) && (
                <Field label={providerStatus?.provider === 'lulu' ? 'Lulu Job ID' : 'ShipStation Order ID'}>
                  {providerStatus?.providerOrderId || diagnostics?.shipstationOrderId || order?.shipstationOrderId}
                </Field>
              )}
              {(diagnostics?.trackingNumber || order?.trackingNumber) && (
                <Field label="Tracking">
                  <span className="flex items-center gap-2 flex-wrap justify-end">
                    {diagnostics?.carrier || order?.carrier ? `${diagnostics?.carrier || order?.carrier} · ` : ''}
                    {diagnostics?.trackingNumber || order?.trackingNumber}
                    <button
                      type="button"
                      className="bg-storyblue text-white rounded px-2 py-0.5 text-xs"
                      onClick={() => copyToClipboard(diagnostics?.trackingNumber || order?.trackingNumber)}
                    >
                      Copy
                    </button>
                  </span>
                </Field>
              )}
              {(diagnostics?.shippedAt || order?.shippedAt) && (
                <Field label="Shipped at">{convertTimestampToDateTime(diagnostics?.shippedAt || order?.shippedAt)}</Field>
              )}
              {showRouteInfo && diagnostics?.routeAmbiguous && (
                <p className="text-xs text-amber-700 mt-2">
                  Country “{diagnostics?.shippingCountry || '—'}” isn’t recognized, so the original purchase used{' '}
                  <strong>{diagnostics?.route === 'shipstation' ? 'ShipStation' : 'Lulu'}</strong> while a re-push would use{' '}
                  <strong>{diagnostics?.repushRoute === 'shipstation' ? 'ShipStation' : 'Lulu'}</strong>. Fix the country to remove the ambiguity.
                </p>
              )}

              {/* Provider actions — talk to ShipStation/Lulu. Release children have no per-order
                  provider actions: their shipment IS the release's combined order. */}
              <div className="flex flex-wrap gap-2 items-center border-t border-gray-200 pt-3 mt-3">
                {isReleaseChild && (
                  <span className="text-xs text-gray-500">
                    Provider actions live on classroom release <b>#{order?.parentBulkPrintOrderNumber || order?.shipmentOrderNumber}</b>.
                  </span>
                )}
                {!isReleaseChild && (
                <button
                  type="button"
                  disabled={anyActionBusy}
                  onClick={handleSync}
                  title="Read-only — pulls the current state from ShipStation and updates our status. Never writes to the provider."
                  className="bg-storyblue text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {syncLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
                  Sync from provider
                </button>
                )}
                {isReleaseChild ? null : !repushIsForce ? (
                  <button
                    type="button"
                    disabled={shipLoading || repushDisabled}
                    onClick={handleRepush}
                    title={repushDisabled ? PHASE_NOTE[phase] || 'Not available in this state' : 'Send this order to ShipStation.'}
                    className="bg-storygreen text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50 flex items-center gap-2"
                  >
                    {shipLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
                    Re-push to Shipping
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={shipLoading || repushDisabled}
                    onClick={handleRepush}
                    title="This order already reached a provider — re-pushing may create a duplicate shipment. Confirms first."
                    className="bg-amber-600 text-white font-semibold rounded-md px-3 py-1.5 text-sm disabled:opacity-50 flex items-center gap-2"
                  >
                    {shipLoading && <Loader loader={true} dimentions={'w-4 h-4'} />}
                    Force re-push (already sent)
                  </button>
                )}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Status timeline — the order's fulfillmentStatus history (primary doc's events subcollection). */}
      {physical && !isBulk && events.length > 0 && (
        <Card title="Status Timeline">
          <ol className="text-sm flex flex-col gap-1">
            {events.map((e, i) => {
              const to = fulfillmentStatusBadge(e?.to);
              return (
                <li key={e?.id || i} className="flex flex-wrap items-center gap-2 py-0.5">
                  <span className="text-gray-500 text-xs whitespace-nowrap">{convertTimestampToDateTime(e?.at)}</span>
                  {e?.from && <span className="text-gray-400 text-xs">{fulfillmentStatusBadge(e.from).label} →</span>}
                  <Badge tone={to.tone}>{to.label}</Badge>
                  <span className="text-gray-400 text-xs">{e?.actor || ''}</span>
                  {e?.meta?.note && <span className="text-gray-400 text-xs italic">“{e.meta.note}”</span>}
                </li>
              );
            })}
          </ol>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Order */}
        <Card title="Order">
          <Field label="Order #">{order?.orderNumber || '—'}</Field>
          <Field label="Item">{orderItemLabel(order)}</Field>
          {physical && !isBulk && <Field label="Book ID">{order?.bookId || '—'}</Field>}
          {physical && !isBulk && diagnostics && (
            <Field label="Resolves by ID">{diagnostics.resolvesById ? 'Yes' : 'No'}</Field>
          )}
          {order?.orderType === OrderTypeEnum.BULK_PRINT && (
            <Field label="Orders in release">{order?.totalBooksInBulk ?? order?.bulkPrintOrderNumbers?.length ?? '—'}</Field>
          )}
          <Field label="Quantity">{order?.quantity}</Field>
          <Field label="Date">{convertTimestampToDateTime(order?.timestamp)}</Field>
        </Card>

        {/* Plan / Credits — subscription & credits only */}
        {(isSubscription || isCredits) && (
          <Card title={isSubscription ? 'Subscription' : 'Image Credits'}>
            {isSubscription && <Field label="Plan">{order?.extraInfo?.plan || '—'}</Field>}
            <Field label="Credits">{order?.extraInfo?.credits ?? (isCredits ? order?.quantity : '—')}</Field>
            {isSubscription && <Field label="Unlimited downloads">{order?.extraInfo?.unlimitedDownloads || '—'}</Field>}
            {order?.extraInfo?.additionImageCreditsInfo && (
              <Field label="Add-on credits">{order.extraInfo.additionImageCreditsInfo}</Field>
            )}
          </Card>
        )}

        {/* Pricing */}
        <Card title="Pricing">
          <Field label="Price">{money(order?.price)}</Field>
          <Field label="Additional page cost">{money(order?.additionalPageCost)}</Field>
          {physical && <Field label="Shipping">{money(order?.shippingAmount)}</Field>}
          <Field label="Coupon">{order?.coupon || '-'}</Field>
          <Field label="Discount">{order?.couponDiscount ? money(order.couponDiscount) : '-'}</Field>
          <div className="border-t mt-2 pt-2">
            <Field label="Total">
              <span className="font-bold">$ {calculateOrderTotal(order)}</span>
            </Field>
          </div>
        </Card>

        {/* Shipping address — physical only */}
        {physical && (
          <Card title="Shipping Address">
            <Field label="Name">{order?.customerName || '—'}</Field>
            <Field label="Address 1">{order?.shippingAddress1}</Field>
            <Field label="Address 2">{order?.shippingAddress2 || '-'}</Field>
            <Field label="City">{order?.shippingCity}</Field>
            <Field label="State">{order?.shippingState}</Field>
            <Field label="Zip">{order?.shippingZip}</Field>
            <Field label="Country">{country || '-'}</Field>
            <Field label="Email">{order?.shippingEmail}</Field>
          </Card>
        )}

        {/* Billing address — physical only */}
        {physical && (
          <Card title="Billing Address">
            <Field label="Cardholder">{order?.billingCardHolderName}</Field>
            <Field label="Address 1">{order?.billingAddress1}</Field>
            <Field label="Address 2">{order?.billingAddress2 || '-'}</Field>
            <Field label="City">{order?.billingCity}</Field>
            <Field label="State">{order?.billingState}</Field>
            <Field label="Postal Code">{order?.billingPostalCode}</Field>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OrderDetailsTable;
